import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BaseWebsocketMessage,
  DelWebsocketMessageRequest,
  InsWebsocketMessageRequest,
  QueryWebsocketMessageRequest,
  SubWebsocketMessageRequest,
  UpdWebsocketMessageRequest,
  WebsocketEntTypes,
  WebSocketService,
  WSEntityManager,
} from '@qtek/core/websockets-core';
import {
  AlloPreferences,
  BackendResponse,
  ChatStatusPayload,
  ChatStreamPayload,
} from '@qtek/shared/models';
import { filter, map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AiChatService {
  private ws = inject(WebSocketService);
  private http = inject(HttpClient);

  // SD-2537: the v2 registry keys tasks by the JPA entity class name ('Chat' / 'ChatEvent'),
  // not v1's lowercase 'chat' / 'chatEvent'.
  private readonly entChat: WebsocketEntTypes = 'Chat';
  private readonly entEvent: WebsocketEntTypes = 'ChatEvent';

  private chatMgr = new WSEntityManager(this.ws, this.entChat);
  private eventMgr = new WSEntityManager(this.ws, this.entEvent);

  readonly chatStatus$: Observable<ChatStatusPayload> = this.ws
    .getWebSocket$()
    .pipe(
      filter((msg: any) => msg.ent === 'chatStatus' && msg.op === 'upd'),
      map((msg: any) => msg.res as ChatStatusPayload)
    );

  /**
   * SD-2155 / SD-2153: incremental token deltas the BE pushes during an Allo
   * response. Wire-only stream (no DDB); the canonical {@link AiChatEvent}
   * still arrives at the end with the same `ts`, at which point the in-flight
   * bubble built from these deltas reconciles against it. Mirrors
   * {@link chatStatus$} for the tool-progress wire path.
   */
  readonly chatStream$: Observable<ChatStreamPayload> = this.ws
    .getWebSocket$()
    .pipe(
      filter((msg: any) => msg.ent === 'chatStream' && msg.op === 'upd'),
      map((msg: any) => msg.res as ChatStreamPayload)
    );

  /**
   * SD-2096: emits when the signed-in user's Allo memory state changed on the BE — most often
   * because the memory extractor folded a preference in after a bot turn, which happens
   * asynchronously in the kafka worker and so has no moment the FE could detect on its own.
   *
   * Signal only: the frame carries no preference content, so consumers re-read
   * {@link getChatPreferences}. Owner-scoped on the BE (`WsChatPrefListener`), so a frame arriving
   * here always concerns the session user.
   */
  readonly chatPrefChanged$: Observable<void> = this.ws.getWebSocket$().pipe(
    filter((msg: any) => msg.ent === 'chatPref' && msg.op === 'upd'),
    map((): void => undefined)
  );

  /**
   * SD-2526 Stage B: emits once each time the websocket (re)connects and finishes its init
   * handshake. Consumers use it to replay an in-flight stream that dropped during the outage.
   */
  readonly reconnected$: Observable<boolean> = this.ws.init$;

  /**
   * SD-2526 Stage B: ask the BE to replay the in-flight `chatStream` frames this client missed
   * during a reconnect. The BE replays the frames after `lastSeq` (our last contiguous seq) as
   * normal `chatStream` `upd` frames — which flow back through {@link chatStream$} — or a single
   * `resyncMiss`. Fire-and-forget; params go through `prms` (Map<String,String> on the BE).
   */
  resyncStream(payload: { chatId: string; ts: number; lastSeq: number }): void {
    // ent is tagged 'chatStream' (the reply channel) for self-description; the BE routes purely on
    // op:'resync' + id, so it's advisory. Sent via the ChatEvent manager only to reuse its socket.
    this.eventMgr.sendWsMessage({
      op: 'resync',
      ent: 'chatStream',
      id: payload.chatId,
      prms: { ts: String(payload.ts), lastSeq: String(payload.lastSeq) },
    });
  }

  /**
   * SD-2765: stop the in-flight Allo turn for this chat.
   *
   * Fire-and-forget by design — the BE answers on the turn's own thread with the terminal
   * `cancelled` stream signal and the persisted partial, so a reply here would only race that.
   * Nothing to cancel is not an error either: a stop landing just as a turn finishes is an ordinary
   * race. Goes through `sendWsMessage` so it inherits the socket-alive gating the other ops use.
   */
  cancelStream(chatId: string): void {
    this.eventMgr.sendWsMessage({ op: 'cancel', id: chatId });
  }

  subscribeChats(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>
  ): Observable<BaseWebsocketMessage> {
    const clean = { ...payload };
    delete (clean as any).cmd;
    return this.chatMgr.subscribeEntity(clean, false);
  }

  queryChats(payload: Omit<QueryWebsocketMessageRequest, 'ent' | 'op'>) {
    this.chatMgr.query(payload);
  }

  createChat(payload: Omit<InsWebsocketMessageRequest, 'ent' | 'op'>) {
    const minimal: any = { ...(payload || {}) };
    delete minimal.res;
    this.chatMgr.create(minimal);
  }

  updateChat(payload: Omit<UpdWebsocketMessageRequest, 'ent' | 'op'>) {
    this.chatMgr.update(payload);
  }

  /**
   * SD-2661: delete a chat. The BE soft-deletes (stamps `deletedAt`) rather than removing the row,
   * so the broadcast that comes back to other sessions is an `upd` carrying `deletedAt` — not a
   * `del`. {@code AiChatStore} handles both.
   */
  deleteChat(payload: Omit<DelWebsocketMessageRequest, 'ent' | 'op'>) {
    this.chatMgr.delete(payload);
  }

  subscribeChatEvents(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>
  ): Observable<BaseWebsocketMessage> {
    const clean = { ...payload };
    delete (clean as any).cmd;
    return this.eventMgr.subscribeEntity(clean, false);
  }

  queryChatEvents(payload: Omit<QueryWebsocketMessageRequest, 'ent' | 'op'>) {
    this.eventMgr.query(payload);
  }

  sendEvent(payload: Omit<InsWebsocketMessageRequest, 'ent' | 'op'>) {
    this.eventMgr.create(payload);
  }

  /**
   * SD-2724: rate one Allo reply. `messageId` is the `chatId.ts` composite the
   * feedback component builds; the BE resolves it to the ChatEvent row. Moved to
   * `/api/v2` when the endpoint was ported to http-server-v2 — the v1 route was
   * removed with v1, so the Submit button 404'd.
   */
  submitFeedback(body: {
    messageId: string;
    rating: 'positive' | 'negative';
    comment?: string;
  }): Observable<unknown> {
    return this.http.post('/api/v2/allo/feedback', body, {
      withCredentials: true,
    });
  }

  /**
   * SD-2326: resolves a proposeAction pending approval. Body shape matches the
   * BE's `AlloApprovalRequest`. Edited values override the proposed `values`
   * map on the card; omit `editedFields` to accept the BE's proposed values
   * unchanged. `decision='reject'` resumes the graph without applying anything.
   */
  submitApproval(body: {
    chatId: string;
    pendingApprovalId: string;
    decision: 'approve' | 'reject';
    editedFields?: Record<string, unknown>;
    reason?: string;
  }): Observable<unknown> {
    return this.http.post('/api/v2/allo/approve-action', body, {
      withCredentials: true,
    });
  }

  /**
   * SD-2096: the caller's cross-session memory state — the preference summary Allo built from
   * their chats, plus their opt-out flag. Always scoped to the session user; the endpoint takes
   * no user id.
   */
  getChatPreferences(): Observable<BackendResponse<AlloPreferences>> {
    return this.http.get<BackendResponse<AlloPreferences>>(
      '/api/v2/allo/preferences',
      { withCredentials: true }
    );
  }

  /**
   * SD-2096: turn cross-session memory off (`optOut = true`) or back on. Opting out stops both
   * extraction and injection but keeps the stored summary, so opting back in restores it.
   */
  setChatMemoryOptOut(
    optOut: boolean
  ): Observable<BackendResponse<AlloPreferences>> {
    return this.http.put<BackendResponse<AlloPreferences>>(
      '/api/v2/allo/preferences/opt-out',
      { optOut },
      { withCredentials: true }
    );
  }

  /**
   * SD-2096: forget the inferred preference summary. The opt-out flag survives — the BE stores it
   * on the same row, so clearing must not double as an opt-in.
   */
  clearChatPreferences(): Observable<BackendResponse<AlloPreferences>> {
    return this.http.delete<BackendResponse<AlloPreferences>>(
      '/api/v2/allo/preferences',
      { withCredentials: true }
    );
  }
}
